import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageCircle, 
  Calendar, 
  Phone, 
  FileText,
  X,
  Lightbulb,
  ChevronRight,
  User,
  Users,
  RefreshCw,
  Send,
  Bot,
  Loader2,
  PhoneCall,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { MASCOT } from '../assets/whyChooseUsImages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback tips if API fails
const FALLBACK_TIPS = [
  { tip: "Check your HVAC filters monthly - dirty filters reduce efficiency by 15%!", category: "HVAC", source: "Energy.gov" },
  { tip: "Know where your main water shut-off valve is located - it could save your home from water damage in an emergency.", category: "Plumbing", source: "PHCC" },
  { tip: "Test GFCI outlets monthly by pressing the 'Test' button, then reset. Replace if they don't trip.", category: "Electrical", source: "NFPA" },
  { tip: "Clean your dryer vent and lint trap regularly - lint buildup is a leading cause of home fires.", category: "General", source: "NFPA" },
  { tip: "Pre-drill holes when screwing near the edge of wood to prevent splitting.", category: "Carpentry", source: "Fine Woodworking" },
];

// Quick reply suggestions
const QUICK_REPLIES = [
  "What services do you offer?",
  "How much does painting cost?",
  "Do you offer free estimates?",
  "What are your business hours?",
  "I need emergency repairs"
];

const InteractiveMascot = () => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const [currentTip, setCurrentTip] = useState(null);
  const [loadingTip, setLoadingTip] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { type: 'bot', text: "Hi! I'm Handy, your AI-powered home repair assistant. I can answer questions about our services, pricing, scheduling, and give you expert home repair advice. How can I help you today?" }
  ]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackInfo, setCallbackInfo] = useState({ name: '', phone: '', email: '', service: '' });
  const [showQuickReplies, setShowQuickReplies] = useState(true);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Generate session ID on first chat open
  useEffect(() => {
    if (showChat && !sessionId) {
      setSessionId(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    }
  }, [showChat, sessionId]);

  // Fetch a random tip from API
  const fetchRandomTip = async () => {
    setLoadingTip(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tips/random?source_type=mixed`);
      setCurrentTip(response.data);
    } catch (error) {
      console.error('Error fetching tip:', error);
      const fallback = FALLBACK_TIPS[Math.floor(Math.random() * FALLBACK_TIPS.length)];
      setCurrentTip({ ...fallback, tip_type: 'fallback' });
    }
    setLoadingTip(false);
  };

  useEffect(() => {
    if (showTip && !currentTip) {
      fetchRandomTip();
    }
  }, [showTip]);

  useEffect(() => {
    if (showTip) {
      const interval = setInterval(() => {
        fetchRandomTip();
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [showTip]);

  // Static mascot image (transparent PNG)
  const staticMascot = MASCOT;

  const quickActions = [
    { icon: Calendar, label: 'Book Now', action: () => navigate('/book'), color: 'bg-green-500' },
    { icon: FileText, label: 'Get Quote', action: () => navigate('/book'), color: 'bg-blue-500' },
    { icon: Phone, label: 'Call Us', action: () => window.location.href = 'tel:505-219-7816', color: 'bg-orange-500' },
  ];

  const handleChatSubmit = async (e, quickReplyText = null) => {
    if (e) e.preventDefault();
    const messageText = quickReplyText || userInput;
    if (!messageText.trim()) return;

    // Hide quick replies after first message
    setShowQuickReplies(false);

    // Add user message
    setChatMessages(prev => [...prev, { type: 'user', text: messageText }]);
    setUserInput('');
    setIsTyping(true);

    try {
      // Call AI chatbot backend
      const response = await axios.post(`${BACKEND_URL}/api/chatbot/chat`, {
        message: messageText,
        session_id: sessionId
      });

      const { response: aiResponse, should_escalate, escalation_reason } = response.data;

      // Add AI response
      setChatMessages(prev => [...prev, { type: 'bot', text: aiResponse }]);

      // Handle escalation
      if (should_escalate) {
        if (escalation_reason === 'emergency') {
          setChatMessages(prev => [...prev, { 
            type: 'system', 
            text: '🚨 For emergencies, please call us immediately at (505) 219-7816',
            isEmergency: true
          }]);
        } else if (escalation_reason === 'human_requested' || escalation_reason === 'complaint') {
          setShowCallbackForm(true);
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      // Fallback to basic response
      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        text: "I'm having trouble connecting right now. For immediate help, please call us at (505) 219-7816. We're here to help!"
      }]);
    }

    setIsTyping(false);
  };

  const handleQuickReply = (text) => {
    handleChatSubmit(null, text);
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!callbackInfo.name || !callbackInfo.phone) return;

    try {
      await axios.post(`${BACKEND_URL}/api/chatbot/collect-info`, {
        session_id: sessionId,
        name: callbackInfo.name,
        phone: callbackInfo.phone,
        email: callbackInfo.email,
        service_needed: callbackInfo.service,
        message: "Customer requested callback from chatbot"
      });

      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        text: `Thanks ${callbackInfo.name}! Paolo will call you at ${callbackInfo.phone} within 24 hours. Is there anything else I can help you with?`
      }]);
      setShowCallbackForm(false);
      setCallbackInfo({ name: '', phone: '', email: '', service: '' });

    } catch (error) {
      console.error('Callback submission error:', error);
      setChatMessages(prev => [...prev, { 
        type: 'bot', 
        text: "I couldn't save your info. Please call us directly at (505) 219-7816."
      }]);
    }
  };

  const clearChat = () => {
    setChatMessages([
      { type: 'bot', text: "Hi! I'm Handy, your AI-powered home repair assistant. I can answer questions about our services, pricing, scheduling, and give you expert home repair advice. How can I help you today?" }
    ]);
    setShowQuickReplies(true);
    setSessionId(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  };

  return (
    <div className="relative">
      {/* Mascot Image */}
      <div
        className="relative cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        onClick={() => setShowMenu(!showMenu)}
      >
        <div className={`transition-transform duration-300 ease-out ${isHovered ? 'scale-125' : 'scale-100'}`}>
          <img
            src={staticMascot}
            alt="A Quality Handyman Mascot"
            className="h-12 w-12 object-contain"
            data-testid="mascot-image"
          />
        </div>
        
        {isHovered && !showMenu && (
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50">
            Click me for help!
          </div>
        )}
      </div>

      {/* Quick Actions Menu */}
      {showMenu && (
        <div 
          className="absolute top-14 left-0 bg-white rounded-xl shadow-2xl p-4 z-50 min-w-[200px] border border-slate-200"
          onMouseLeave={() => setShowMenu(false)}
        >
          <div className="flex items-center justify-between mb-3 pb-2 border-b">
            <span className="font-semibold text-slate-800">How can I help?</span>
            <button onClick={() => setShowMenu(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Quick Actions */}
          <div className="space-y-2 mb-3">
            {quickActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => { action.action(); setShowMenu(false); }}
                className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                data-testid={`quick-action-${idx}`}
              >
                <div className={`${action.color} p-1.5 rounded-lg text-white`}>
                  <action.icon className="w-4 h-4" />
                </div>
                <span className="text-slate-700 font-medium">{action.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t pt-2 space-y-2">
            {/* Chat Button */}
            <button
              onClick={() => { setShowChat(true); setShowMenu(false); }}
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-blue-50 transition-colors text-blue-600"
              data-testid="open-chat-btn"
            >
              <Bot className="w-5 h-5" />
              <span className="font-medium">Chat with Handy AI</span>
            </button>
            
            {/* Tips Button */}
            <button
              onClick={() => { setShowTip(true); setShowMenu(false); }}
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-yellow-50 transition-colors text-yellow-600"
              data-testid="show-tip-btn"
            >
              <Lightbulb className="w-5 h-5" />
              <span className="font-medium">Home Repair Tip</span>
            </button>
          </div>

          {/* Portal Access Links */}
          <div className="border-t pt-2 mt-2 space-y-2">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-2">Portal Access</div>
            <button
              onClick={() => { navigate('/customer/login'); setShowMenu(false); }}
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-purple-50 transition-colors text-purple-600"
              data-testid="mascot-customer-portal-btn"
            >
              <User className="w-5 h-5" />
              <span className="font-medium">Customer Portal</span>
            </button>
            <button
              onClick={() => { navigate('/employee/login'); setShowMenu(false); }}
              className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-indigo-50 transition-colors text-indigo-600"
              data-testid="mascot-employee-portal-btn"
            >
              <Users className="w-5 h-5" />
              <span className="font-medium">Employee Portal</span>
            </button>
          </div>
        </div>
      )}

      {/* AI Chat Window - Using Portal to render at body level */}
      {showChat && createPortal(
        <div className="fixed bottom-4 right-4 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl z-[9999] overflow-hidden border border-slate-200 flex flex-col max-h-[80vh]" data-testid="chat-window">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-brand-blue to-brand-teal p-4 flex items-center gap-3 flex-shrink-0">
            <div className="relative">
              <img src={staticMascot} alt="Handy" className="w-10 h-10 object-contain" />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <Bot className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-white flex items-center gap-2">
                Handy AI
                <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">Powered by AI</span>
              </h4>
              <p className="text-xs text-white/80">Your smart home repair assistant</p>
            </div>
            <button 
              onClick={clearChat}
              className="text-white/60 hover:text-white mr-1"
              title="Clear chat"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setShowChat(false)}
              className="text-white/80 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50 min-h-[200px] max-h-[400px]">
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.type === 'system' ? (
                  <div className={`w-full p-3 rounded-xl text-center ${msg.isEmergency ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-blue-100 text-blue-700'}`}>
                    <div className="flex items-center justify-center gap-2">
                      {msg.isEmergency && <AlertTriangle className="w-4 h-4" />}
                      {msg.text}
                    </div>
                    {msg.isEmergency && (
                      <a 
                        href="tel:505-219-7816" 
                        className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-colors"
                      >
                        <PhoneCall className="w-4 h-4" />
                        Call Now
                      </a>
                    )}
                  </div>
                ) : (
                  <div className={`max-w-[85%] p-3 rounded-2xl ${
                    msg.type === 'user' 
                      ? 'bg-brand-blue text-white rounded-br-sm' 
                      : 'bg-white text-slate-700 rounded-bl-sm shadow-sm border border-slate-100'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  </div>
                )}
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-slate-500 p-3 rounded-2xl rounded-bl-sm shadow-sm border border-slate-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Handy is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Quick Replies */}
            {showQuickReplies && chatMessages.length === 1 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {QUICK_REPLIES.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickReply(reply)}
                    className="text-xs bg-white border border-brand-blue text-brand-blue px-3 py-1.5 rounded-full hover:bg-brand-blue hover:text-white transition-colors"
                    data-testid={`quick-reply-${idx}`}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Callback Form */}
          {showCallbackForm && (
            <div className="p-4 bg-blue-50 border-t border-blue-100">
              <p className="text-sm text-blue-800 mb-3 font-medium">
                I'd be happy to have Paolo call you back! Please share your info:
              </p>
              <form onSubmit={handleCallbackSubmit} className="space-y-2">
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={callbackInfo.name}
                  onChange={(e) => setCallbackInfo(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  required
                  data-testid="callback-name"
                />
                <input
                  type="tel"
                  placeholder="Phone Number *"
                  value={callbackInfo.phone}
                  onChange={(e) => setCallbackInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  required
                  data-testid="callback-phone"
                />
                <input
                  type="email"
                  placeholder="Email (optional)"
                  value={callbackInfo.email}
                  onChange={(e) => setCallbackInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  data-testid="callback-email"
                />
                <select
                  value={callbackInfo.service}
                  onChange={(e) => setCallbackInfo(prev => ({ ...prev, service: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  data-testid="callback-service"
                >
                  <option value="">What do you need help with?</option>
                  <option value="General Repairs">General Repairs</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Carpentry">Carpentry</option>
                  <option value="Painting">Painting</option>
                  <option value="Home Maintenance">Home Maintenance</option>
                  <option value="Other">Other</option>
                </select>
                <div className="flex gap-2">
                  <Button 
                    type="submit" 
                    size="sm" 
                    className="flex-1 bg-brand-blue hover:bg-brand-blue-dark"
                    data-testid="callback-submit"
                  >
                    Request Callback
                  </Button>
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowCallbackForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}
          
          {/* Input */}
          {!showCallbackForm && (
            <form onSubmit={handleChatSubmit} className="p-3 border-t bg-white flex-shrink-0">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Ask me anything about home repairs..."
                  className="flex-1 px-4 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  disabled={isTyping}
                  data-testid="chat-input"
                />
                <Button 
                  type="submit" 
                  size="sm" 
                  className="rounded-full bg-brand-blue hover:bg-brand-blue-dark px-4"
                  disabled={isTyping || !userInput.trim()}
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 text-center">
                AI-powered by GPT-4o, Claude & Gemini
              </p>
            </form>
          )}
        </div>,
        document.body
      )}

      {/* Tip Popup - Using Portal */}
      {showTip && createPortal(
        <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
          <div 
            className="absolute inset-0 bg-black/30"
            onClick={() => { setShowTip(false); setCurrentTip(null); }}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden border-2 border-yellow-400 animate-in zoom-in-95 duration-200">
            <div className="bg-yellow-400 p-3 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-800" />
              <span className="font-bold text-yellow-800">Home Repair Tip</span>
              <button 
                onClick={() => { setShowTip(false); setCurrentTip(null); }}
                className="ml-auto text-yellow-800/80 hover:text-yellow-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {loadingTip ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-6 h-6 animate-spin text-yellow-600" />
                </div>
              ) : currentTip ? (
                <>
                  <p className="text-slate-700 leading-relaxed">{currentTip.tip}</p>
                  <div className="flex justify-between items-center mt-4 pt-3 border-t">
                    <div className="text-xs">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">{currentTip.category}</span>
                      <span className="text-slate-400 ml-2">Source: {currentTip.source}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-slate-400 capitalize">
                      {currentTip.tip_type === 'ai_generated' ? 'AI Generated' : 
                       currentTip.tip_type === 'scraped' ? 'Web Source' : 'Expert Tip'}
                    </span>
                    <button 
                      onClick={fetchRandomTip}
                      className="text-sm text-brand-blue hover:underline flex items-center gap-1"
                      disabled={loadingTip}
                    >
                      <RefreshCw className={`w-3 h-3 ${loadingTip ? 'animate-spin' : ''}`} />
                      Next tip
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-center py-4">Loading tip...</p>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default InteractiveMascot;
